<template>
  <v-layout wrap>
    <v-flex xs12 pb-0>
      <v-layout wrap align-center>
        <v-flex xs12 sm6 md6 lg6>
          <h1 class="headline">{{ $t('investors.title') }}</h1>
        </v-flex>
        <v-flex xs12 sm6 md6 lg6>
          <v-layout mt-2 wrap align-center justify-end>
            <export-button
              v-if="$can('investor_export')"
              class="ml-auto mr-3"
              action-name="investor/downloadList"
              large
              :file-name="$t('investors.title')"
              :query="{
                searchString: search,
                status: investorStatus.id,
              }"
            />

            <v-btn
              v-if="$can('investor_add')"
              type="submit"
              :to="{ name: 'add-investor' }"
              color="warning"
              large
              depressed
            >
              {{ $t('common.btns.addInvestor') }}
            </v-btn>
          </v-layout>
        </v-flex>
      </v-layout>
      <v-layout wrap align-center>
        <v-flex
          xs12
          sm12
          md6
          lg5
          mt-4
          :class="{ 'mt-4': $vuetify.breakpoint.mdAndUp }"
        >
          <v-layout wrap align-center class="search-wrapper">
            <v-text-field
              class="search"
              ref="search"
              v-model="search"
              :placeholder="$t('investorsList.search.placeholder')"
              autocomplete="off"
              solo
            ></v-text-field>
            <v-btn
              type="button"
              class="search-btn warning mb-4"
              depressed
              large
              @click="searchInvestors"
            >
              {{ $t('common.btns.search') }}
            </v-btn>
          </v-layout>
        </v-flex>
        <v-flex
          d-flex
          xs12
          sm12
          md5
          lg6
          :class="{ 'mt-4': $vuetify.breakpoint.mdAndUp }"
          class="filter-wrapper ml-4"
        >
          <v-select
            :items="investorsStatuses"
            v-model="investorStatus"
            :label="$t('common.fields.status')"
            item-text="title"
            item-value="id"
            class="filter mr-3"
            :class="{ 'ml-4': $vuetify.breakpoint.mdAndUp }"
            @change="changeInvestorStatus($event)"
            return-object
          />
          <v-select
            :items="investorsLocations"
            v-model="investorLocation"
            :label="$t('common.fields.region')"
            item-text="title"
            item-value="id"
            class="filter"
            :class="{ 'ml-4': $vuetify.breakpoint.mdAndUp }"
            clearable
            @change="searchInvestors"
          />
        </v-flex>
      </v-layout>
    </v-flex>
    <v-flex xs12>
      <v-data-table
        :items="investorsList.items"
        :headers="headers"
        :loading="isLoading"
        :loading-text="$t('common.table.dataIsLoading')"
        :server-items-length="2"
        :mobile-breakpoint="100"
        class="table-custom investorsList small mb-4"
        hide-default-footer
      >
        <template slot="item" slot-scope="{ item }">
          <tr @click="goToInvestorPage(item.id)">
            <td>{{ item.num }}</td>
            <td class="text-xs-left">
              <v-layout align-center justify-start>
                {{ item.id }}
              </v-layout>
            </td>
            <td class="text-xs-left">{{ item.name }}</td>
            <td class="text-xs-left">{{ item.phone }}</td>
            <td class="text-xs-left">{{ item.email }}</td>
            <td class="text-xs-left company">{{ item.companies }}</td>
            <td class="text-xs-left">{{ item.status }}</td>
            <td class="text-xs-left">{{ item.location }}</td>
          </tr>
        </template>
        <template slot="no-data">{{ $t('common.table.noData') }}</template>
      </v-data-table>
      <div v-if="paginationPage" class="text-xs-center">
        <v-pagination
          :value="paginationPage"
          :length="paginationLength"
          @input="choosePaginatorPage($event)"
        ></v-pagination>
      </div>
    </v-flex>
  </v-layout>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from 'vuex';
import ExportButton from '@/components/shared/ExportButton.vue';

export default {
  name: 'InvestorsListPage',
  components: { ExportButton },
  data() {
    return {
      search: '',
      investorLocation: '',
      headers: [
        { text: this.$t('common.number'), sortable: false },
        { text: this.$t('investorsList.tableHeaders.id'), sortable: false },
        { text: this.$t('investorsList.tableHeaders.name'), sortable: false },
        { text: this.$t('investorsList.tableHeaders.phone'), sortable: false },
        { text: this.$t('investorsList.tableHeaders.email'), sortable: false },
        {
          text: this.$t('investorsList.tableHeaders.company'),
          sortable: false,
        },
        { text: this.$t('investorsList.tableHeaders.status'), sortable: false },
        { text: this.$t('auto.fields.location'), sortable: false },
      ],
    };
  },
  watch: {
    /* eslint-disable */
    '$i18n.locale': async function() {
      await this.fetchInvestorsProperties();
      await this.fetchInvestors({
        page: this.paginationPage,
        searchString: this.search,
        investorStatus: this.investorStatus.id.toString(),
      });
      this.$vuetify.goTo(0, 'easeInOutCubic');
    },
  },
  computed: {
    ...mapGetters({
      isLoading: 'global/getLoader',
      investorsList: 'investor/getInvestorsListData',
      investorsStatuses: 'investor/getInvestorsStatuses',
      investorsLocations: 'investor/getInvestorsLocations',
      status: 'investor/getInvestorStatus',
    }),
    investorStatus: {
      get() {
        return this.status || {};
      },
      set(value) {
        this.setInvestorStatus(value);
      },
    },
    paginationLength() {
      return this.investorsList.lastPage;
    },
    paginationPage() {
      if (
        this.investorsList.currentPage === 1 &&
        this.investorsList.lastPage === 1
      ) {
        return 0;
      }
      return this.investorsList.currentPage;
    },
  },
  async created() {
    await this.fetchInvestorsProperties();
    await this.fetchInvestors({
      page: 1,
      investorStatus: this.investorStatus.id.toString(),
    });
  },
  destroyed() {
    this.setInvestorsListData({});
    this.setInvestorsStatuses([]);
    this.setInvestorsLocations([]);
    this.setInvestorStatus(null);
    this.clearAlertError();
  },
  methods: {
    ...mapActions({
      fetchInvestors: 'investor/fetchInvestorsListData',
      fetchInvestorsProperties: 'investor/fetchInvestorsProperties',
    }),
    ...mapMutations({
      setInvestorsListData: 'investor/setInvestorsListData',
      setInvestorsStatuses: 'investor/setInvestorsStatuses',
      setInvestorStatus: 'investor/setInvestorStatus',
      setInvestorsLocations: 'investor/setInvestorsLocations',
      clearAlertError: 'global/clearAlertError',
    }),
    getSearchParams(page = 1) {
      return {
        page,
        searchString: this.search,
        investorStatus: this.investorStatus.id.toString(),
        investorLocation: this.investorLocation,
      };
    },
    async searchInvestors() {
      await this.fetchInvestors(this.getSearchParams());
    },
    async choosePaginatorPage(page) {
      await this.fetchInvestors(this.getSearchParams(page));
      this.$vuetify.goTo(0, 'easeInOutCubic');
    },
    goToInvestorPage(investorId) {
      this.$router.push(`/investors/${investorId}`);
    },
    async changeInvestorStatus(status) {
      this.setInvestorStatus(status);
      await this.searchInvestors();
    },
  },
};
</script>

<style lang="scss" scoped>
.search-wrapper {
  .search {
    margin-right: -12px;
    margin-bottom: -12px;
    max-width: 400px;

    @media (max-width: 400px) {
      margin-right: 0px;
      margin-bottom: -12px;
      max-width: 100%;
    }
  }

  @media (max-width: 400px) {
    flex-direction: column;

    .search {
      width: 100%;
    }
  }
}
</style>
